import { useCallback, useRef, useState } from "react";
import { useAIReasoningStream } from "./useAIReasoningStream.ts";
import { stripCodeFences } from "utils/aiText.ts";
import { parse as parsePartial, OBJ as ALLOW_OBJ, ARR as ALLOW_ARR, STR as ALLOW_STR } from "partial-json";

export interface UseAIJSONStreamOptions<T> {
  buildPrompt: () => string;
  normalizeOrNull: (value: unknown) => T | null;
  onFinal?: (value: T, reasoning: string) => void;
  initialValue?: T | null;
}

export function useAIJSONStream<T>(options: UseAIJSONStreamOptions<T>) {
  const { buildPrompt, normalizeOrNull, onFinal, initialValue = null } = options;
  const [value, setValue] = useState<T | null>(initialValue);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [rawText, setRawText] = useState<string>("");
  const { streaming, reasoningText, latestReasoningRef, start, cancel } = useAIReasoningStream();
  const tryParseRef = useRef<(text: string) => void>();

  tryParseRef.current = (text: string) => {
    try {
      const cleaned = stripCodeFences(text);
      if (cleaned.replace(/\s+/g, "") === "") return;
      const parsed = parsePartial(cleaned, ALLOW_OBJ | ALLOW_ARR | ALLOW_STR) as unknown;
      const maybe = normalizeOrNull(parsed);
      if (maybe) setValue(maybe);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.debug("partial-json parse pending:", e);
    }
  };

  const requestStreaming = useCallback(async () => {
    setLoading(true);
    setError(null);
    setValue(null);
    setRawText("");
    try {
      const prompt = buildPrompt();
      await start(prompt, {
        onToken: (_t, all) => {
          setRawText(all);
          tryParseRef.current?.(all);
        },
        onDone: () => {
          setValue((prev) => {
            if (prev && onFinal) onFinal(prev, latestReasoningRef.current);
            return prev;
          });
        },
        onError: (e) => {
          setError(e instanceof Error ? e.message : String(e));
        },
      });
    } catch (e) {
      const err = e as unknown as { name?: string };
      if (err && err.name === "AbortError") {
        setError(null);
      } else {
        setError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setLoading(false);
    }
  }, [buildPrompt, onFinal, start, latestReasoningRef]);

  return {
    value,
    loading,
    error,
    requestStreaming,
    cancel,
    streaming,
    rawText,
    reasoningText,
  } as const;
}

